import { useEffect, useState, useMemo, Fragment } from "react";
import {
  GoabCircularProgress,
  GoabBlock,
  GoabSpacer,
  GoabButtonGroup,
  GoabButton,
  GoabModal,
} from "@abgov/react-components";
import ApplicationComponent from "./ApplicationComponent";
import NewApplicationListComponent from "./NewApplicationListComponent";
import { getAvailableProgramEvents } from "../../MPP-Common-SPA/utils/getAvailableProgramEvents";
import {
  StatusTransitionEventEnum,
  InternalApplicationStatuses,
} from "../../MPP-Common-SPA/common/constants/statuses";

const ACTIVE_INTERNAL_STATUS_IDS = new Set([
  //These IDs are deemed active 2, 3, 4, 5, 6, 7, 10, 11, 12, 13, 14, 15, 17, 20,
  InternalApplicationStatuses.Draft, //2
  InternalApplicationStatuses.Submitted, //3
  InternalApplicationStatuses.AutoApproved, //4
  InternalApplicationStatuses.InProgress, //5
  InternalApplicationStatuses.FollowUp, //6
  InternalApplicationStatuses.Approved, //7
  InternalApplicationStatuses.EOAuditPending, //10
  InternalApplicationStatuses.ReadyToSend, //11
  InternalApplicationStatuses.SentWithAutoAuthorization, //12
  InternalApplicationStatuses.PaymentAuthorized, //13
  InternalApplicationStatuses.PaymentOnHold, //14
  InternalApplicationStatuses.SentBackByEO, //15
  InternalApplicationStatuses.PaymentProcessing, //17
  InternalApplicationStatuses.EOAuthorized, //20
]);

const ApplicationList = (props) => {
  const {
    applicationList = [],
    isLoading,
    programEvents,
    handleWithdrawApplication,
    getResourceValue,
    program,
    user,
    isEventsEnabled,
  } = props;
  const availableEvents = getAvailableProgramEvents(
    programEvents,
    applicationList,
  );
  const [showResubmitModal, setShowResubmitModal] = useState(false);

  useEffect(() => {
    if (!isLoading && applicationList?.length > 0) {
      const hasSubmittedToDraft = applicationList.some(
        (app) =>
          app.statusTransitionEvent ===
            StatusTransitionEventEnum.SubmittedToDraft &&
          app.internalStatus === InternalApplicationStatuses.Draft,
      );

      setShowResubmitModal(hasSubmittedToDraft);
    }
  }, [isLoading, applicationList]);

  const sortedApplicationList = useMemo(() => {
    const isActive = (app) =>
      ACTIVE_INTERNAL_STATUS_IDS.has(app.internalStatus);

    return applicationList?.length > 0
      ? [...applicationList].sort((a, b) => {
          const aActive = isActive(a);
          const bActive = isActive(b);
          if (aActive !== bActive) {
            return aActive ? -1 : 1; // Active applications first
          }
          return new Date(b.updatedTime) - new Date(a.updatedTime); // Most recent first
        })
      : [];
  }, [applicationList]);
  return (
    <>
      <GoabModal
        heading="Review your applications"
        open={showResubmitModal}
        calloutVariant="important"
        onClose={() => setShowResubmitModal(false)}
        actions={
          <GoabButtonGroup alignment="end">
            <GoabButton
              type="primary"
              size="compact"
              onClick={() => setShowResubmitModal(false)}
            >
              Continue
            </GoabButton>
          </GoabButtonGroup>
        }
      >
        <p>
          One or more of your applications have been updated and moved back to a
          draft state.
        </p>
        <br></br>
        <p>Please review the information in your applications.</p>
      </GoabModal>
      <GoabCircularProgress
        variant="fullscreen"
        visible={isLoading}
        message={getResourceValue({ key: "RESOURCES@global.loading" })}
      />
      {!isLoading && sortedApplicationList?.length > 0 && (
        <>
          {sortedApplicationList.map((application) => (
            <Fragment key={application.id}>
              <ApplicationComponent
                program={program}
                application={application}
                programEvents={programEvents}
                availableEvents={availableEvents}
                handleWithdrawApplication={handleWithdrawApplication}
              />
              <GoabSpacer vSpacing="xl" />
            </Fragment>
          ))}
        </>
      )}
      {!isLoading && sortedApplicationList?.length === 0 && (
        <GoabBlock direction="column" gap="2xl" alignment="center">
          <img
            src="/Images/paymentInfo.svg"
            alt=""
            style={{ width: "278px", height: "137px" }}
          />
          <h3 className="secondary">
            {getResourceValue({
              key: "RESOURCES@myApplications.noActiveApplications",
            })}
          </h3>
        </GoabBlock>
      )}
      {!isLoading && !applicationList && (
        <GoabBlock direction="column" gap="m" alignment="center">
          <GoabSpacer vSpacing="xl" />
          <h3>Failed to load applications.</h3>
        </GoabBlock>
      )}
      <NewApplicationListComponent
        availableEvents={availableEvents}
        getResourceValue={getResourceValue}
        program={program}
        user={user}
        applicationList={applicationList}
        programEvents={programEvents}
      />
      <GoabSpacer vSpacing="3xl" />
    </>
  );
};

export default ApplicationList;
